import i18next from "i18next";
import { z } from "zod";
import {
  FIELD_END_DATE_LESS_THAN_NOW,
  FIELD_MAX_LENGTH_SELECT,
  FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
  FIELD_NUMBER_NEGATIVE,
  FIELD_REQUIRED,
  START_DATE_AND_END_DATE_VALIDATION,
} from "../../../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_APP_PROMOTION_TYPE_RETARGETING_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  TIKTOK,
  tiktokCurrenciesData,
} from "../../../../../utils/constants";
import { getIntegrationKey } from "../../../../../actions/getters";
import { getMinimumBudget } from "../../../../../services/adsCampaigns";

export const adGroupTiktokBaseSchema = z.object({
  ads: z.array(z.any()).optional(),
  platform: z.literal(TIKTOK),
  typeBudget: z.any().optional(),
  budget: z.any().optional().default(0),

  startDate: z.any().optional(),
  endDate: z.any().optional(),
  ageRange: z.tuple(
    [
      z.number().min(18, i18next.t(FIELD_REQUIRED)),
      z.number().max(65, i18next.t(FIELD_REQUIRED)),
    ],
    {
      required_error: i18next.t(FIELD_REQUIRED),
    }
  ),
  genders: z.object(
    {
      male: z.boolean().optional(),
      female: z.boolean().optional(),
    },
    {
      required_error: i18next.t(FIELD_REQUIRED),
    }
  ),
  locations: z.array(z.any()).optional(),
  interests: z.array(z.any()).optional(),
  behaviors: z.array(z.any()).optional(),

  trackingID: z.any().optional(),
  trackingCustomEventID: z.any().optional(),

  customAudiences: z.any().optional(),
  excludedCustomAudiences: z.any().optional(),

  appID: z.any().optional(),
  appOS: z.any().optional(),
  catalogID: z.any().optional(),
  productSetAudience: z.any().optional(),

  bidGoal: z.any().optional(),

  placements: z.any().optional(),
});

export const verifyTikTokBudget = ({
  useCampaignBudget,
  typeBudget,
  budget,
  integrations,
  integrationKey,
  integrationID,
  startDate,
  endDate,
}) => {
  const integrationsTikTok = integrations[integrationKey] || {};
  const integration = integrationsTikTok?.[integrationID];

  if (!integration) {
    return { ok: true };
  }

  const currency = integration?.currency;
  const currencyData = tiktokCurrenciesData[currency];

  if (!currencyData) {
    return { ok: true };
  }

  const budgetMinimum = getMinimumBudget({
    platform: TIKTOK,
    typeBudget,
    startDateTime: startDate,
    endDateTime: endDate,
    currency,
    useCampaignBudget,
  });

  const numericBudget = Number(budget || 0);

  if (numericBudget < budgetMinimum) {
    return {
      ok: false,
      message: FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
      value: budgetMinimum,
      currency,
    };
  }

  return { ok: true };
};

export const validateAdGroupTikTok = ({
  campaign,
  integrations,
  adGroup,
  indexAdGroup,
  ctx,
}) => {
  const basicPaths = ["adsGroups", indexAdGroup];
  const statusCampaign = campaign.states?.[TIKTOK];
  const isStatusDraft = statusCampaign === ADS_CAMPAIGN_STATUS_DRAFT;
  const platformData = campaign.platformsData?.[TIKTOK];
  const useCampaignBudget = platformData?.useCampaignBudget;
  const typeBudgetCampaign = platformData?.typeBudget;
  const objective = platformData?.objective;
  const campaignType = platformData?.type;

  const isAppPromotionCampaign =
    objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION;
  const isAppRetargetingCampaign =
    campaignType === ADS_CAMPAIGN_APP_PROMOTION_TYPE_RETARGETING_OPTION;
  const isCatalogCampaignTikTok =
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
    campaignType === ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION;

  if (adGroup.genders) {
    const { male = false, female = false } = adGroup.genders;
    if (!male && !female) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "genders"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  } else {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "genders"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (!adGroup.locations || adGroup.locations.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "locations"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  if (adGroup.bidGoal && adGroup.bidGoal < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "bidGoal"],
      message: i18next.t(FIELD_NUMBER_NEGATIVE),
    });
  }

  if (!useCampaignBudget) {
    const integrationKey = getIntegrationKey(TIKTOK, true);
    const integrationID = platformData?.integrationID;
    const budgetValidation = verifyTikTokBudget({
      useCampaignBudget: false,
      typeBudget: adGroup.typeBudget,
      budget: adGroup.budget,
      integrations,
      integrationKey,
      integrationID,
      startDate: adGroup.startDate,
      endDate: adGroup.endDate,
    });

    if (!budgetValidation.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "budget"],
        message: i18next.t(budgetValidation.message, {
          value: budgetValidation.value,
          dailyValue: budgetValidation.dailyValue,
          currency: budgetValidation.currency,
        }),
      });
    }
  }

  if (
    adGroup.typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION ||
    typeBudgetCampaign === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION
  ) {
    if (!adGroup.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "startDate"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (!adGroup.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "endDate"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (adGroup.startDate && adGroup.endDate) {
      const startDate = new Date(adGroup.startDate);
      const endDate = new Date(adGroup.endDate);
      const now = new Date();

      if (endDate.getTime() < now.getTime() && isStatusDraft) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "endDate"],
          message: i18next.t(FIELD_END_DATE_LESS_THAN_NOW),
        });
      }
      if (startDate.getTime() > endDate.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "startDate"],
          message: i18next.t(START_DATE_AND_END_DATE_VALIDATION),
        });
      }
    }
  }

  // Pixel validation - skip for catalog (sales + app)
  if (
    ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK.includes(objective) &&
    !isCatalogCampaignTikTok
  ) {
    if (!adGroup.trackingID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "trackingID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (!adGroup.trackingCustomEventID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "trackingCustomEventID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  }

  if (isAppPromotionCampaign || isCatalogCampaignTikTok) {
    if (!adGroup.appID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "appID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (isCatalogCampaignTikTok && !adGroup.catalogID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "catalogID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (isAppRetargetingCampaign || isCatalogCampaignTikTok) {
      if (!adGroup.trackingCustomEventID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "trackingCustomEventID"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    }
  }

  if (
    adGroup?.customAudiences?.length > 0 &&
    adGroup?.customAudiences?.length > 20
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "customAudiences"],
      message: i18next.t(FIELD_MAX_LENGTH_SELECT, {
        value: 20,
      }),
    });
  }

  if (
    adGroup?.excludedCustomAudiences?.length > 0 &&
    adGroup?.excludedCustomAudiences?.length > 20
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "excludedCustomAudiences"],
      message: i18next.t(FIELD_MAX_LENGTH_SELECT, {
        value: 20,
      }),
    });
  }
};
